'use client';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Popover } from '@tranquility-ai/theme-components/popover';
import { TpTooltip } from '@tranquility-ai/theme-components/tooltip';
import '@tranquility-ai/theme-components/popover.css';
import { createPortal } from 'react-dom';
import dynamic from 'next/dynamic';
import { Slide, MediaType, View } from '@/shared/types';
import { encodeEvidenceLockerStyleProxyUriSegment } from '@/shared/functions';
import { hasTimelineDate, sortSlidesForHorizontalTimeline } from '@/shared/clientFunctions';
import { resolveCourtroomCopy } from '@/shared/courtroomCopy';
import { MediaDisplay } from './ui/mediaDisplay';
import {
  CaretLeftIcon,
  CaretRightIcon,
  ArrowsOutSimpleIcon,
  ArrowsInSimpleIcon,
  Minus,
  Plus,
  TextAaIcon,
  ArrowsLeftRightIcon,
  ArrowsDownUpIcon,
} from '@phosphor-icons/react';
import { useSwipeable } from 'react-swipeable';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { PresentationMarkdown } from './panes/presentationMarkdown';
import { useAppFormatters } from '@/i18n/useAppFormatters';
import { useTimePilotT } from '@/i18n/useTimePilotT';

import type { CourtroomEventGridSlide } from './tp-courtroom-event-grid-modal';

const TpCourtroomEventGridModal = dynamic(
  () => import('./tp-courtroom-event-grid-modal').then((m) => m.TpCourtroomEventGridModal),
  { ssr: false, loading: () => null },
);

dayjs.extend(utc);
dayjs.extend(timezone);

// Constants
const TITLE_SLIDE_ID = 'TITLE_SLIDE' as const;
const UNTIMED_DATE_GROUP_KEY = '__UNDATED__' as const;

// Font size constants
const FONT_SIZE_BASE = {
  title: 3,        // 3rem (48px) - equivalent to text-5xl
  body: 1.875,     // 1.875rem (30px) - equivalent to text-3xl
  date: 1.875,     // 1.875rem (30px) - equivalent to text-3xl
  eventNumber: 1.25, // 1.25rem (20px) - equivalent to text-xl
} as const;

const FONT_SIZE_INCREMENT = 0.25;
const FONT_SIZE_SCALE_DEFAULT = 0;

const ICON_SIZE_DEFAULT = 24;

// Color constants

// Title slide default values
const TITLE_SLIDE_DISPLAY_ORDER = 0;
const TITLE_SLIDE_INDEX = 0;
const TITLE_SLIDE_DATE_GROUP_INDEX = 0;
const TITLE_SLIDE_TOTAL_EVENTS = 1;

// Convert storage path or S3 URL to proxy URL (Evidence Locker path shape)
const convertS3UrlToProxyUrl = (url: string | undefined): string => {
  const t = (url ?? '').trim();
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t;
  return `/api/proxy/${encodeEvidenceLockerStyleProxyUriSegment(t)}`;
};

interface TPCourtroomProps {
  title?: string;
  description?: string;
  courtroomTitle?: string;
  courtroomDescription?: string;
  media?: string;
  allSlides: Slide[];
  /** Leave courtroom (typically back to slides / presentation). */
  onExitFullscreen?: () => void;
}

interface ProcessedSlide extends Slide {
  slideIndex: number;
  dateGroupIndex: number;
  totalEventsOnDate: number;
  displayDate: string;
}

const TPCourtroom: React.FC<TPCourtroomProps> = ({
  title,
  description,
  courtroomTitle,
  courtroomDescription,
  media,
  allSlides,
  onExitFullscreen,
}) => {
  const t = useTimePilotT();
  const { formatDate: formatLocaleDate } = useAppFormatters();
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [fontSizeScale, setFontSizeScale] = useState<number>(FONT_SIZE_SCALE_DEFAULT);
  const [isMediaFullscreen, setIsMediaFullscreen] = useState(false);
  const [fontSizeDropdownOpen, setFontSizeDropdownOpen] = useState(false);
  const [isEventGridModalOpen, setIsEventGridModalOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(true); // Default to fullscreen for courtroom view
  const [isHorizontalLayout, setIsHorizontalLayout] = useState(true); // Default to horizontal layout on desktop
  // Only portal after mount so SSR markup matches (document.body is unavailable
  // during server render).
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => setIsMounted(true), []);

  // ========== Slide Processing ==========

  // Process slides: flatten and add metadata
  const processedSlides = React.useMemo(() => {
    const slides: ProcessedSlide[] = [];

    // Add title slide if present
    if (title || description || media || courtroomTitle?.trim() || courtroomDescription?.trim()) {
      slides.push({
        id: TITLE_SLIDE_ID,
        displayOrder: TITLE_SLIDE_DISPLAY_ORDER,
        slideTitle: resolveCourtroomCopy(courtroomTitle, title) || t('workProducts.timeline'),
        slideDescription: resolveCourtroomCopy(courtroomDescription, description),
        media: media ? [{ mediaSrc: media, mediaType: 'image' as MediaType }] : undefined,
        show: true,
        slideIndex: TITLE_SLIDE_INDEX,
        dateGroupIndex: TITLE_SLIDE_DATE_GROUP_INDEX,
        totalEventsOnDate: TITLE_SLIDE_TOTAL_EVENTS,
        displayDate: '',
      });
    }

    // Include every visible slide — undated events stay after dated ones (same
    // ordering as the horizontal timeline).
    const visibleSlides = allSlides.filter((slide) => slide.show);
    const sortedSlides = [...visibleSlides].sort(sortSlidesForHorizontalTimeline);

    const eventsByDate = new Map<string, Slide[]>();
    sortedSlides.forEach((slide) => {
      const dateKey = hasTimelineDate(slide) ? slide.date! : UNTIMED_DATE_GROUP_KEY;
      const group = eventsByDate.get(dateKey);
      if (group) {
        group.push(slide);
      } else {
        eventsByDate.set(dateKey, [slide]);
      }
    });

    // Process events and add metadata
    let slideIndex = slides.length;
    sortedSlides.forEach((event) => {
      const dateKey = hasTimelineDate(event) ? event.date! : UNTIMED_DATE_GROUP_KEY;
      const eventsOnDate = eventsByDate.get(dateKey)!;
      const eventIndex = eventsOnDate.indexOf(event);
      const displayDate = hasTimelineDate(event)
        ? (event.displayDate || event.date || dateKey)
        : (event.displayDate || t('presentation.dateNotSpecified'));

      slides.push({
        ...event,
        slideTitle: resolveCourtroomCopy(event.courtroomTitle, event.slideTitle),
        slideDescription: resolveCourtroomCopy(event.courtroomDescription, event.slideDescription),
        slideIndex: slideIndex++,
        dateGroupIndex: eventIndex,
        totalEventsOnDate: eventsOnDate.length,
        displayDate,
      });
    });

    return slides;
  }, [allSlides, title, description, courtroomTitle, courtroomDescription, media, t]);

  const currentSlide = processedSlides[currentSlideIndex];
  const totalSlides = processedSlides.length;

  // ========== Date Utilities ==========

  // Format date for display
  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = dayjs(dateStr);
    if (!date.isValid()) return dateStr;
    // Date-only strings are UTC midnight for Intl. Format the calendar day
    // dayjs already resolved so US timezones do not show the previous day.
    return formatLocaleDate(
      new Date(date.year(), date.month(), date.date()),
      { year: 'numeric', month: 'long', day: '2-digit' },
    );
  };

  // Get event number text
  const getEventNumberText = (
    slide: Pick<CourtroomEventGridSlide, 'id' | 'dateGroupIndex' | 'totalEventsOnDate'>,
  ): string => {
    if (slide.id === TITLE_SLIDE_ID) return '';
    return t('courtroom.slideOf', {
      current: slide.dateGroupIndex + 1,
      total: slide.totalEventsOnDate,
    });
  };

  // Calculate date range and total events for title slide
  const { dateRange, totalEvents } = React.useMemo(() => {
    const eventSlides = allSlides.filter((slide) => slide.show);
    const totalEvents = eventSlides.length;

    if (totalEvents === 0) {
      return { dateRange: '', totalEvents: 0 };
    }

    const dates = eventSlides
      .filter((slide) => hasTimelineDate(slide))
      .map((slide) => slide.date!)
      .map((date) => dayjs(date));

    if (dates.length === 0) {
      return { dateRange: '', totalEvents };
    }

    // Find min and max dates
    let minDate = dates[0];
    let maxDate = dates[0];
    dates.forEach(date => {
      if (date.isBefore(minDate)) {
        minDate = date;
      }
      if (date.isAfter(maxDate)) {
        maxDate = date;
      }
    });

    // Format date range
    let dateRangeStr = '';
    if (minDate.isSame(maxDate, 'day')) {
      // Same day
      dateRangeStr = minDate.format('MMMM D, YYYY');
    } else if (minDate.isSame(maxDate, 'month') && minDate.isSame(maxDate, 'year')) {
      // Same month and year
      dateRangeStr = `${minDate.format('MMMM D')} – ${maxDate.format('D, YYYY')}`;
    } else if (minDate.isSame(maxDate, 'year')) {
      // Same year, different months
      dateRangeStr = `${minDate.format('MMMM D')} – ${maxDate.format('MMMM D, YYYY')}`;
    } else {
      // Different years
      dateRangeStr = `${minDate.format('MMMM D, YYYY')} – ${maxDate.format('MMMM D, YYYY')}`;
    }

    return { dateRange: dateRangeStr, totalEvents };
  }, [allSlides]);

  // ========== Font Size Utilities ==========

  // Calculate font sizes based on scale (rem-based)
  const getFontSizes = (scale: number) => {
    return {
      title: `${FONT_SIZE_BASE.title + (scale * FONT_SIZE_INCREMENT)}rem`,
      body: `${FONT_SIZE_BASE.body + (scale * FONT_SIZE_INCREMENT)}rem`,
      date: `${FONT_SIZE_BASE.date + (scale * FONT_SIZE_INCREMENT)}rem`,
      eventNumber: `${FONT_SIZE_BASE.eventNumber + (scale * FONT_SIZE_INCREMENT)}rem`,
    };
  };

  const currentFontSizes = useMemo(() => getFontSizes(fontSizeScale), [fontSizeScale]);

  // ========== Navigation Utilities ==========

  // Navigation functions
  const goToSlide = useCallback((index: number) => {
    if (index >= 0 && index < totalSlides) {
      setCurrentSlideIndex(index);
    }
  }, [totalSlides]);

  const goToNextSlide = useCallback(() => {
    if (currentSlideIndex < totalSlides - 1) {
      setCurrentSlideIndex(prev => prev + 1);
    }
  }, [currentSlideIndex, totalSlides]);

  const goToPreviousSlide = useCallback(() => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(prev => prev - 1);
    }
  }, [currentSlideIndex]);

  // Handle event card click
  const handleEventCardClick = (index: number) => {
    goToSlide(index);
    setIsEventGridModalOpen(false);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Prevent font size shortcuts when typing in inputs
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (e.key === 'ArrowLeft') {
        goToPreviousSlide();
      } else if (e.key === 'ArrowRight') {
        goToNextSlide();
      } else if (e.key === '+' || e.key === '=') {
        // Plus key or equals key (shifted plus)
        e.preventDefault();
        setFontSizeScale(prev => prev + 1);
      } else if (e.key === '-' || e.key === '_') {
        // Minus key or underscore (shifted minus)
        e.preventDefault();
        setFontSizeScale(prev => prev - 1);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [goToNextSlide, goToPreviousSlide]);

  // Swipe gestures
  const swipeHandlers = useSwipeable({
    onSwipedLeft: goToNextSlide,
    onSwipedRight: goToPreviousSlide,
    trackMouse: true,
  });

  // ========== Effects ==========

  // Handle fullscreen toggle
  useEffect(() => {
    if (isFullscreen) {
      document.body.classList.add('courtroom-fullscreen');
    } else {
      document.body.classList.remove('courtroom-fullscreen');
    }
    return () => {
      document.body.classList.remove('courtroom-fullscreen');
    };
  }, [isFullscreen]);

  if (!currentSlide) {
    return (
      <div className="flex items-center justify-center h-full bg-transparent text-white">
        <div className="text-2xl">{t('courtroom.noEvents')}</div>
      </div>
    );
  }

  const courtroomBody = (
    <div
      className={`flex flex-col h-full text-white overflow-hidden ${isFullscreen ? 'fixed inset-0 z-[100000] bg-gray-900' : 'bg-transparent'}`}
      {...swipeHandlers}
    >

      {/* Top control bar - in fullscreen it is pinned to the viewport (mirrors
          the bottom pagination bar) and stays above the timeline content; the
          app nav is hidden via the `courtroom-fullscreen` body class. When not
          fullscreen it flows in-page so it sits below the app nav instead of
          covering it. */}
      <div className={`${isFullscreen ? 'fixed top-0 left-0 right-0 z-[30001]' : 'relative z-10'} flex items-start justify-between gap-4 p-8 pb-4 bg-transparent`}>
        {/* Event context: date and event number */}
        <div className="flex items-baseline gap-4 min-w-0">
          {currentSlide.id === TITLE_SLIDE_ID ? (
            <>
              {dateRange && (
                <div className="font-bold truncate" style={{ fontSize: currentFontSizes.date }}>
                  {dateRange}
                </div>
              )}
              {totalEvents > 0 && (
                <div className="font-bold text-white/70 flex-shrink-0" style={{ fontSize: currentFontSizes.eventNumber }}>
                  {t(totalEvents === 1 ? 'workProducts.eventCountOne' : 'workProducts.eventCountMany', { count: totalEvents })}
                </div>
              )}
            </>
          ) : (
            <>
              {currentSlide.displayDate && (
                <div className="font-bold truncate" style={{ fontSize: currentFontSizes.date }}>
                  {formatDate(currentSlide.displayDate)}
                </div>
              )}
              {getEventNumberText(currentSlide) && (
                <div className="font-bold text-white/70 flex-shrink-0" style={{ fontSize: currentFontSizes.eventNumber }}>
                  {getEventNumberText(currentSlide)}
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex items-start gap-2 flex-shrink-0">
          {/* Layout Toggle */}
          <TpTooltip content={isHorizontalLayout ? t('courtroom.verticalLayout') : t('courtroom.horizontalLayout')} placement="bottom" delayDuration={0}>
            <Button
              iconOnly
              size="md"
              style="text"
              color="light"
              background="inverse"
              onClick={() => setIsHorizontalLayout(!isHorizontalLayout)}
              className="hidden lg:inline-flex"
              aria-label={isHorizontalLayout ? t('courtroom.verticalLayout') : t('courtroom.horizontalLayout')}
            >
              {isHorizontalLayout ? (
                <ArrowsLeftRightIcon size={ICON_SIZE_DEFAULT} />
              ) : (
                <ArrowsDownUpIcon size={ICON_SIZE_DEFAULT} />
              )}
            </Button>
          </TpTooltip>
          {/* Font Size Control */}
          <TpTooltip
            content={t('courtroom.fontSize')}
            placement="bottom"
            delayDuration={0}
            disabled={fontSizeDropdownOpen}
            contentStyle={{ zIndex: 100001 }}
          >
            <div className="inline-flex">
              <Popover
                isOpen={fontSizeDropdownOpen}
                onOpenChange={setFontSizeDropdownOpen}
                variant="inverted"
                side="bottom"
                align="center"
                sideOffset={8}
                maxWidth="max-content"
                aria-label={t('courtroom.fontSize')}
                content={
                  <div className="flex items-center gap-2" role="group" aria-label={t('courtroom.fontSize')}>
                    <Button
                      type="button"
                      iconOnly
                      size="md"
                      style="text"
                      onClick={() => setFontSizeScale((prev) => prev - 1)}
                      aria-label={t('courtroom.decreaseFont')}
                    >
                      <Minus />
                    </Button>
                    <Button
                      type="button"
                      iconOnly
                      size="md"
                      style="text"
                      onClick={() => setFontSizeScale((prev) => prev + 1)}
                      aria-label={t('courtroom.increaseFont')}
                    >
                      <Plus />
                    </Button>
                  </div>
                }
              >
                <Button
                  iconOnly
                  size="md"
                  style={fontSizeDropdownOpen ? 'solid' : 'text'}
                  color="light"
                  background="inverse"
                  aria-label={t('courtroom.fontSize')}
                  aria-pressed={fontSizeDropdownOpen}
                  onClick={() => setFontSizeDropdownOpen((open) => !open)}
                >
                  <TextAaIcon />
                </Button>
              </Popover>
            </div>
          </TpTooltip>
          <TpTooltip
            content={isFullscreen ? t('courtroom.backToSlides') : t('courtroom.enterFullscreen')}
            placement="bottom"
            delayDuration={0}
            contentStyle={{ zIndex: 100001 }}
          >
            <Button
              iconOnly
              size="md"
              style="text"
              color="light"
              background="inverse"
              onClick={() => {
                if (isFullscreen && onExitFullscreen) {
                  onExitFullscreen();
                  return;
                }
                setIsFullscreen((current) => !current);
              }}
              className="flex-shrink-0"
              aria-label={isFullscreen ? 'Back to slides' : 'Enter fullscreen'}
            >
              {isFullscreen ? (
                <ArrowsInSimpleIcon size={ICON_SIZE_DEFAULT} />
              ) : (
                <ArrowsOutSimpleIcon size={ICON_SIZE_DEFAULT} />
              )}
            </Button>
          </TpTooltip>
        </div>
      </div>

      {/* Main content area (extra top padding only when the control bar is
          pinned/overlayed in fullscreen so it does not cover the content). */}
      <div className={`flex-1 flex flex-col min-h-0 px-8 pb-24 ${isFullscreen ? 'pt-28' : 'pt-0'}`}>
        {/* Title, Media and Details - Vertically centered flex container */}
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar2">
          <div className="min-h-full flex flex-col justify-center">
          {/* Media and Content side-by-side or left-aligned if no media */}
          {currentSlide.media && currentSlide.media.length > 0 ? (
            <div className={`grid grid-cols-1 ${isHorizontalLayout ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-16 items-start`}>

              {/* Media Section */}
              <div className="flex flex-col">
                <div className="relative">
                  <div className="relative h-[50vh] w-full aspect-video bg-black/20 rounded-lg overflow-hidden">
                    <MediaDisplay
                      mediaItem={currentSlide.media[0]}
                      isPresentation={true}
                      hideMediaChrome={true}
                    />
                  </div>

                </div>
              </div>

              {/* Title and Details Section */}
              <div className="flex flex-col gap-0">
                {/* Title */}
                {currentSlide.slideTitle && (
                  <h1 className="font-bold leading-tight m-0" style={{ fontSize: currentFontSizes.title }}>
                    {currentSlide.slideTitle}
                  </h1>
                )}

                {/* Details */}
                {currentSlide.slideDescription && (
                  <div className="leading-normal [&_p]:leading-relaxed" style={{ fontSize: currentFontSizes.body }}>
                    <PresentationMarkdown inheritFontSize>{currentSlide.slideDescription}</PresentationMarkdown>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-start">
              {/* Title */}
              {currentSlide.slideTitle && (
                <h1 className="font-bold mb-8 leading-tight" style={{ fontSize: currentFontSizes.title }}>
                  {currentSlide.slideTitle}
                </h1>
              )}
              {currentSlide.slideDescription && (
                <div className="leading-normal max-w-4xl [&_p]:leading-relaxed" style={{ fontSize: currentFontSizes.body }}>
                  <PresentationMarkdown inheritFontSize>{currentSlide.slideDescription}</PresentationMarkdown>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Bottom Pagination - Fixed to viewport */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-white/10 bg-transparent p-4 flex items-center justify-center gap-4 z-40">
        <Button
          iconOnly
          size="md"
          style="text"
          color="light"
          background="inverse"
          onClick={goToPreviousSlide}
          disabled={currentSlideIndex === 0}
          className="disabled:opacity-30"
          aria-label={t('presentation.previousSlide')}
        >
          <CaretLeftIcon size={ICON_SIZE_DEFAULT} />
        </Button>

        {/* Visual Pagination Dots */}
        <div className="flex items-center gap-2 px-4">
          {processedSlides.map((slide, index) => (
            <TpTooltip
              key={index}
              content={
                <div className="flex flex-col gap-1">
                <div className="font-semibold">
                {slide.id === TITLE_SLIDE_ID
                ? dateRange || t('workProducts.timeline')
                : formatDate(slide.displayDate || slide.date || '')
              }
                </div>
                {slide.id !== TITLE_SLIDE_ID && getEventNumberText(slide) && (
                <div className="text-sm text-white/70">
                {getEventNumberText(slide)}
                </div>
                )}
                {slide.id === TITLE_SLIDE_ID && totalEvents > 0 && (
                <div className="text-sm text-white/70">
                {t(totalEvents === 1 ? 'workProducts.eventCountOne' : 'workProducts.eventCountMany', { count: totalEvents })}
                </div>
                )}
                </div>
              }
              placement="top"
              delayDuration={0}
              contentStyle={{ zIndex: 100001 }}
            >
              <Button
                iconOnly
                size="sm"
                style="text"
                color="light"
                background="inverse"
                onClick={() => goToSlide(index)}
                className={`transition-all p-0 min-w-0 border-0 ${
                  index === currentSlideIndex
                    ? 'w-3 h-3 bg-white rounded-full'
                    : 'w-2 h-2 bg-white/40 rounded-full hover:bg-white/60'
                }`}
                aria-label={t('courtroom.goToEvent', { number: index + 1 })}
              />
            </TpTooltip>
          ))}
        </div>

        <Button
          size="sm"
          style="text"
          color="light"
          background="inverse"
          onClick={() => setIsEventGridModalOpen(true)}
          className="px-4 py-2"
          aria-label={t('courtroom.openGrid', { current: currentSlideIndex + 1, total: totalSlides })}
        >
          <span className="text-xl font-medium">
            {t('courtroom.slideOf', { current: currentSlideIndex + 1, total: totalSlides })}
          </span>
        </Button>

        <Button
          iconOnly
          size="md"
          style="text"
          color="light"
          background="inverse"
          onClick={goToNextSlide}
          disabled={currentSlideIndex === totalSlides - 1}
          className="disabled:opacity-30"
          aria-label={t('presentation.nextSlide')}
        >
          <CaretRightIcon size={ICON_SIZE_DEFAULT} />
        </Button>
      </div>

      {/* Fullscreen Media Overlay */}
      {isMediaFullscreen && currentSlide.media && currentSlide.media.length > 0 && (
        <div
          className="fixed inset-0 bg-black z-50 flex items-center justify-center p-8"
          onClick={() => setIsMediaFullscreen(false)}
        >
          <div className="relative w-full h-full max-w-7xl max-h-[90vh]">
            <Button
              iconOnly
              size="md"
              style="text"
              color="light"
              background="inverse"
              onClick={() => setIsMediaFullscreen(false)}
              className="absolute top-4 right-4 z-10"
              aria-label={t('courtroom.exitMediaFullscreen')}
            >
              <ArrowsInSimpleIcon size={ICON_SIZE_DEFAULT} />
            </Button>
            <MediaDisplay
              mediaItem={currentSlide.media[0]}
              isPresentation={true}
              hideMediaChrome={true}
            />
          </div>
        </div>
      )}

      <TpCourtroomEventGridModal
        isOpen={isEventGridModalOpen}
        onClose={() => setIsEventGridModalOpen(false)}
        slides={processedSlides}
        formatDate={formatDate}
        getEventNumberText={getEventNumberText}
        onSelectSlide={handleEventCardClick}
      />
    </div>
  );

  // In fullscreen, portal the overlay onto document.body so it escapes the app's
  // stacking contexts and transformed/clipping ancestors and reliably covers the
  // navigation chrome. The `courtroom-fullscreen` body class (toggled above) is
  // kept as a belt-and-suspenders to also hide the nav bars.
  if (isFullscreen && isMounted) {
    return createPortal(courtroomBody, document.body);
  }

  return courtroomBody;
};

export default TPCourtroom;
